import React, { useEffect } from "react";
import { Box } from "@mui/material";

import { QuizProvider, useQuizContext } from "@/state/QuizContext";
import { useQuizStorage } from "@/state/useQuizStorage";
import { SubStepProvider } from "@/state/SubStepContext";
import { ValidationProvider } from "@/state/ValidationContext";
import QuizStepper from "@/components/quiz/QuizStepper";
import ResultsStepper from "@/components/results/ResultsStepper";
import ResultsStepperPV from "@/components/resultsPV/ResultsStepper";
import { useQuizNavigation } from "@/state/useQuizNavigation";
import { PersistedStateV3 } from "@/domains/storage/PersistSchema";
import { readURLState } from "@/state/urlState";
import { StepId } from "@/domains/quiz/StepDefinitions";
import { ProductsProvider } from "@/state/ProductsContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useExperiment } from "@/experiments/useExperiment";
import { PV_TEST_KEY, PV_VARIANT } from "@/experiments/quizTests";


type QuizAppProps = {
  customerJSON?: any;
  bundleProductsJSON?: any[];
  pouchesProductsJSON?: any[];
  treatProductsJSON?: any[];
  supplementProductsJSON?: any[];
  hiddenPriceProductsJSON?: any[];
  planProductMap?: Record<string, string>;
  hiddenPriceMap?: Record<string, string>;
};

type GlobalStep = StepId | "results";

function QuizInner() {
  const { getGlobalStep } = useQuizNavigation();

  // Price & Value test (#51): exposure = quiz start, both arms, deduped per
  // session, so conversion reads as purchases per quiz start. Any variant
  // other than PV_VARIANT (absent, paused, bootstrap failure) renders the
  // control results flow - fail open.
  const pvTest = useExperiment(PV_TEST_KEY);
  const { trackExposure: trackPvTestExposure } = pvTest;
  useEffect(() => {
    trackPvTestExposure();
  }, [trackPvTestExposure]);
  const Results = pvTest.variant === PV_VARIANT ? ResultsStepperPV : ResultsStepper;

  const globalStep = getGlobalStep() as GlobalStep;
  const isResultsMode = globalStep === "results";

  return (
    <Box
      sx={{
        // Quiz canvas is cream (Figma redesign); results flow keeps its blue bg.
        bgcolor: isResultsMode ? "#E1F6FD" : "var(--pop-cream, #faf5eb)",
        minHeight: "100dvh",
        width: "100%",
        overflowX: "hidden",
        pt: "env(safe-area-inset-top)",
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      {isResultsMode ? <Results /> : <QuizStepper />}
    </Box>
  );
}

function QuizStatePersister({
  persist,
}: {
  persist: (s: PersistedStateV3) => void;
}) {
  const { state } = useQuizContext();

  useEffect(() => {
    persist(state as PersistedStateV3);
  }, [state, persist]);

  return null;
}

function QuizURLInitializer() {
  const { state, dispatch } = useQuizContext();

  useEffect(() => {
    const { mode, quizStep, resultsStep, dogId } = readURLState();

    const dogOrder = state.dogOrder;
    const fallbackDogId =
      (dogId && dogOrder.includes(dogId) && dogId) ||
      state.activeDogId ||
      dogOrder[0];

    const effectiveMode =
      mode ?? (resultsStep ? "results" : "quiz");

    if (effectiveMode === "results") {
      dispatch({
        type: "SET_GLOBAL_STEP",
        step: "results",
      });

      // Control has 5 results steps, the Price & Value variant 6; each
      // stepper clamps anything out of its own range back to step 1.
      if (resultsStep && resultsStep >= 1 && resultsStep <= 6) {
        dispatch({
          type: "SET_RESULTS_STEP",
          step: resultsStep,
        });
      }
    } else if (effectiveMode === "quiz") {
      if (!fallbackDogId) return;

      if (dogId && dogOrder.includes(dogId)) {
        dispatch({ type: "SET_ACTIVE_DOG", dogId });
      }


      if (quizStep) {
        dispatch({
          type: "SET_DOG_STEP",
          dogId: fallbackDogId,
          step: quizStep,
        });
        dispatch({
          type: "SET_GLOBAL_STEP",
          step: quizStep,
        });
      }
    }
  }, []);

  return null;
}

export function QuizApp(props: QuizAppProps) {
  const { initial, persist } = useQuizStorage();

  if (!initial) return null;

  return (
    <ErrorBoundary>
      <ProductsProvider
        planProducts={props.bundleProductsJSON || []}
        mealProducts={props.pouchesProductsJSON || []}
        treatProducts={props.treatProductsJSON || []}
        supplementProducts={props.supplementProductsJSON || []}
        hiddenPriceProducts={props.hiddenPriceProductsJSON || []}
        planProductMap={props.planProductMap || {}}
        hiddenPriceMap={props.hiddenPriceMap || {}}
      >
        <QuizProvider initial={initial}>
          <ValidationProvider>
            <SubStepProvider>
              <QuizURLInitializer />
              <QuizStatePersister persist={persist} />
              <QuizInner />
            </SubStepProvider>
          </ValidationProvider>
        </QuizProvider>
      </ProductsProvider>
    </ErrorBoundary>
  );
}